import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import "./styles/globals.css";

// Public pages
import HomePage from "./pages/HomePage";
import RestaurantListPage from "./pages/RestaurantListPage";
import RestaurantPage from "./pages/RestaurantPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import NotFoundPage from "./pages/NotFoundPage";

// User pages
import UserOrdersPage from "./pages/user/UserOrdersPage";
import UserProfilePage from "./pages/user/UserProfilePage";

// Admin pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRestaurants from "./pages/admin/AdminRestaurants";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminRestaurantEdit from "./pages/admin/AdminRestaurantEdit";

// Booking pages
import BookingPage from "./pages/BookingPage";
import MyBookingsPage from "./pages/user/MyBookingsPage";

// AI Assistant
import AIAssistant from "./components/common/AIAssistant";

// Restaurant pages
import RestaurantLayout from "./pages/restaurant/RestaurantLayout";
import RestaurantDashboard from "./pages/restaurant/RestaurantDashboard";
import RestaurantMenu from "./pages/restaurant/RestaurantMenu";
import RestaurantOrders from "./pages/restaurant/RestaurantOrders";
import RestaurantSettings from "./pages/restaurant/RestaurantSettings";
import RestaurantBookings from "./pages/restaurant/RestaurantBookings";

// Route guards
const RequireAuth = ({ children, roles }) => {
  const { user, profile, loading, profileLoading } = useAuth();

  // Still checking session or fetching profile — show spinner
  if (loading || profileLoading) return <PageLoader />;

  // Not logged in — send to login
  if (!user) return <Navigate to="/login" replace />;

  // Logged in but role check fails
  if (roles && profile && !roles.includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-orange-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-500 font-body">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<HomePage />} />
      <Route path="/restaurants" element={<RestaurantListPage />} />
      <Route path="/restaurant/:slug" element={<RestaurantPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/order-success" element={<OrderSuccessPage />} />

      {/* User (any logged in) */}
      <Route
        path="/checkout"
        element={
          <RequireAuth>
            <CheckoutPage />
          </RequireAuth>
        }
      />
      <Route
        path="/my-orders"
        element={
          <RequireAuth roles={["user", "admin"]}>
            <UserOrdersPage />
          </RequireAuth>
        }
      />
      <Route path="/restaurant/:slug/book" element={<BookingPage />} />
      <Route
        path="/my-bookings"
        element={
          <RequireAuth>
            <MyBookingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <UserProfilePage />
          </RequireAuth>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <RequireAuth roles={["admin"]}>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="restaurants" element={<AdminRestaurants />} />
        <Route path="restaurants/new" element={<AdminRestaurantEdit />} />
        <Route path="restaurants/:id/edit" element={<AdminRestaurantEdit />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>

      {/* Restaurant */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth roles={["restaurant", "admin"]}>
            <RestaurantLayout />
          </RequireAuth>
        }
      >
        <Route index element={<RestaurantDashboard />} />
        <Route path="menu" element={<RestaurantMenu />} />
        <Route path="orders" element={<RestaurantOrders />} />
        <Route path="bookings" element={<RestaurantBookings />} />
        <Route path="settings" element={<RestaurantSettings />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRoutes />
            <AIAssistant />
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3000,
                style: {
                  fontFamily: "'DM Sans', sans-serif",
                  borderRadius: "12px",
                  padding: "12px 16px",
                },
                success: { iconTheme: { primary: "#f97316", secondary: "#fff" } },
              }}
            />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
